import { NavLink, useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, apiClient } from "../App";
import { useTheme } from "../context/ThemeContext";
import { 
  LayoutDashboard, 
  FolderKanban, 
  Calendar as CalendarIcon, 
  DollarSign, 
  Settings, 
  LogOut,
  ChevronDown,
  UserPlus,
  X,
  Copy,
  Share2,
  Check,
  CheckCircle,
  Link2,
  Menu,
  Lightbulb,
  Users,
  Plus,
  FileText,
  Clock,
  Briefcase,
  CalendarOff,
  BookOpen,
  Eye,
  PiggyBank,
  Shield,
  Activity,
  Command,
  UserCircle,
  Image,
  Building2,
  Terminal,
  Sun,
  Moon,
  Zap,
  Globe,
  MessageSquare,
  TrendingUp,
  GitBranch,
  Landmark,
  Mic,
  Sparkles,
  ClipboardList,
  Video,
  Mail,
  PanelLeftClose,
  PanelLeftOpen,
  Maximize,
  Minimize,
  LifeBuoy
} from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import NotificationDropdown from "./NotificationDropdown";
import NewRequestDialog from "./help-desk/NewRequestDialog";

// Routes that live in the "Work in Progress" sidebar section. Kept next to the
// section itself so the auto-expand-on-route check can't drift from the item
// list — and checked BEFORE the prefix branches, since paths like
// /sales-pipeline and /financials would otherwise match Sales/Executive.
const WIP_PATHS = [
  "/meetings",
  "/sales/quote-links",
  "/sales-pipeline",
  "/archviz-planner",
  "/review-studio",
  "/financials",
  "/planning-command-center",
  "/ai-workflows",
];

// ============================================
// STATIC LOGO - Never re-renders
// ============================================
const SidebarLogo = memo(function SidebarLogo() {
  return (
    <div className="px-5 pt-6 pb-5 flex-shrink-0">
      <img 
        src="/trp-logo.png"
        alt="The Render Pros" 
        className="h-7 object-contain opacity-[0.92]"
        loading="eager"
        decoding="sync"
      />
      <p className="text-[10px] font-medium tracking-[1.8px] uppercase mt-2.5" style={{ color: 'rgba(255,255,255,0.42)' }}>
        Operations OS
      </p>
    </div>
  );
});

// ============================================
// MEMOIZED NAV ITEM - Apple-esque styling
// ============================================
const NavItem = memo(function NavItem({ path, label, icon: Icon, onNavClick, badge }) {
  return (
    <NavLink
      to={path}
      end={path === "/"}
      onClick={onNavClick}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-all duration-[120ms] ease-out select-none outline-none focus:outline-none focus:ring-0 tracking-[0.02em] ${
          isActive
            ? "text-white"
            : "hover:text-white/90"
        }`
      }
      style={({ isActive }) => ({
        color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
        backgroundColor: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
      })}
      onMouseEnter={(e) => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
        }
      }}
      onMouseLeave={(e) => {
        const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon 
        className="w-4 h-4 transition-all duration-[120ms]" 
        strokeWidth={1.5}
        style={{ opacity: 0.7 }}
      />
      <span className="transition-opacity duration-[120ms] flex-1">
        {label}
      </span>
      {badge > 0 && (
        <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-medium bg-terracotta text-white rounded-full tabular-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
});

// ============================================
// COLLAPSIBLE SECTION - Apple-esque styling
// ============================================
const SidebarSection = memo(function SidebarSection({ 
  id, 
  label, 
  items, 
  isExpanded, 
  onToggle, 
  onNavClick
}) {
  const { t } = useTranslation();
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [items.length]);

  const handleToggle = useCallback(() => {
    onToggle(id);
  }, [onToggle, id]);

  // Helper to get translated label
  const getItemLabel = (item) => item.labelKey ? t(item.labelKey) : item.label;

  return (
    <div className="mb-4">
      <button
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="w-full flex items-center justify-between px-3 py-2 text-[15px] font-medium tracking-[0.02em] rounded-lg transition-all duration-[120ms] ease-out select-none"
        style={{ 
          color: 'rgba(255,255,255,0.72)',
          backgroundColor: isHovered ? 'rgba(255,255,255,0.04)' : 'transparent'
        }}
        data-testid={`section-${id}`}
      >
        <span>{label}</span>
        <ChevronDown 
          className="w-4 h-4 transition-transform duration-200 ease-out"
          style={{ 
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            color: isExpanded 
              ? 'rgba(255,255,255,0.8)' 
              : isHovered 
                ? 'rgba(255,255,255,0.6)' 
                : 'rgba(255,255,255,0.35)'
          }}
        />
      </button>
      
      <div 
        className="overflow-hidden transition-all duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          // `height` is measured once from scrollHeight, which reads 0 when the
          // sidebar mounts hidden (narrow viewport / closed drawer). Falling back
          // to 'none' means an expanded section still shows its items instead of
          // staying clamped shut — for the single-purpose roles the whole sidebar
          // is one section whose toggle is a no-op, so a 0 measurement would
          // otherwise leave them with no navigation at all.
          maxHeight: isExpanded ? (height ? `${height}px` : 'none') : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div 
          ref={contentRef}
          className="space-y-1 pt-1 pl-1 transition-transform duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{ transform: isExpanded ? 'translateY(0)' : 'translateY(-4px)' }}
        >
          {items.map((item) => (
            <NavItem 
              key={item.path}
              path={item.path}
              label={getItemLabel(item)}
              icon={item.icon}
              onNavClick={onNavClick}
              badge={item.badge}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

// ============================================
// USER PROFILE - Apple-esque styling
// ============================================
const UserProfile = memo(function UserProfile({ name, role }) {
  return (
    <div className="mt-4 pt-4 select-none" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center gap-3 px-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
          <UserCircle className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.6)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium truncate tracking-[0.02em]" style={{ color: 'rgba(255,255,255,0.92)' }}>{name}</p>
          <p className="text-[12px] truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>{role}</p>
        </div>
      </div>
    </div>
  );
});

// ============================================
// SIDEBAR NAVIGATION (Memoized separately)
// ============================================
const SidebarNavigation = memo(function SidebarNavigation({
  expandedSections,
  toggleSection,
  onNavClick,
  pmTestMode,
  artistTestMode,
  isPM,
  isArtist,
  isAdmin,
  canViewCosting,
  intakeNotificationCount,
  userEmail,
  userRole
}) {
  const { t } = useTranslation();
  
  // Artist-only sidebar (minimal)
  const artistItems = useMemo(() => [
    { path: "/artist/projects", labelKey: "nav.programs", icon: FolderKanban },
    { path: "/artist/timeline", labelKey: "nav.artistTimeline", icon: CalendarIcon },
    { path: "/artist/time-off", label: "Time Off", icon: CalendarOff },
    { path: "/artist/profile", labelKey: "settings.profile.title", icon: UserCircle },
    { path: "/help-desk", label: "Help Desk", icon: LifeBuoy },
  ], []);

  // VEXOS Executive — single-purpose login, so its whole sidebar is just the
  // dashboard plus the portfolio (mirrors the App.js VexosExecutiveScope whitelist).
  const vexosExecutiveItems = useMemo(() => [
    { path: "/vexos-executive", label: "VEXOS Executive", icon: Command },
    { path: "/portfolio", labelKey: "nav.portfolio", icon: Image },
    { path: "/help-desk", label: "Help Desk", icon: LifeBuoy },
  ], []);

  // Sales Assistant — quoting-only login, so the sidebar is just the two tools
  // it may reach (mirrors the App.js SCOPED_ROLES whitelist for the role).
  const salesAssistantItems = useMemo(() => [
    { path: "/sales/client-calculator", label: "Client Calculator", icon: Sparkles },
    { path: "/upsells", label: "Upsells", icon: TrendingUp },
    { path: "/upsells/opportunities", label: "Upsell Opportunities", icon: Sparkles },
    { path: "/help-desk", label: "Help Desk", icon: LifeBuoy },
  ], []);

  const overviewItems = useMemo(() => [
    { path: "/", labelKey: "nav.today", icon: LayoutDashboard },
    { path: "/help-desk", label: "Help Desk", icon: LifeBuoy },
  ], []);

  const FINANCIALS_ALLOWED = ["bernard@therenderpros.com", "cole@therenderpros.com", "juanpotasman2003@gmail.com"];
  const canViewFinancials = FINANCIALS_ALLOWED.includes((userEmail || "").toLowerCase());
  const canViewChangeOrders = isAdmin || canViewFinancials || isPM;
  const canViewUpsells = isAdmin || canViewFinancials || userRole === "Sales" || isPM;

  const workItems = useMemo(() => [
    { path: "/programs", labelKey: "nav.programs", icon: FolderKanban },
    ...(!isPM && !pmTestMode && !artistTestMode ? [{ path: "/money", label: "Money", icon: DollarSign }] : []),
    { path: "/portfolio", labelKey: "nav.portfolio", icon: Image },
  ], [pmTestMode, artistTestMode, isPM]);

  const salesItems = useMemo(() => {
    // PMs (and admins previewing PM mode) get a FOCUSED Sales section limited to
    // the revenue tools already whitelisted for them — Upsells. Change Orders now
    // lives in Studio for everyone. The full Sales toolkit (calculators, Fathom,
    // pipeline, intake) stays Sales/Admin-only. This is what unhides the Sales
    // tab for PM-role reps.
    if (isPM || pmTestMode) {
      return [
        ...(canViewUpsells ? [
          { path: "/upsells", label: "Upsells", icon: TrendingUp },
          { path: "/upsells/opportunities", label: "Upsell Opportunities", icon: Sparkles },
        ] : []),
      ];
    }
    return [
      { path: "/sales/client-calculator", label: "Client Calculator", icon: Sparkles },
      ...(canViewUpsells ? [
        { path: "/upsells", label: "Upsells", icon: TrendingUp },
        // The recommendation engine — moved off the project page, where it was
        // clutter on a surface people open to run a project, not to sell into it.
        { path: "/upsells/opportunities", label: "Upsell Opportunities", icon: Sparkles },
      ] : []),
      { path: "/fathom/calls", label: "Fathom Calls", icon: Mic },
      { path: "/sales/intake/new", labelKey: "intake.newIntake", icon: Plus },
      { path: "/ops/pipeline", label: "New Projects", icon: GitBranch, badge: intakeNotificationCount },
    ];
  }, [intakeNotificationCount, canViewUpsells, isPM, pmTestMode]);

  const studioItems = useMemo(() => [
    // Admins reach Resources from the Executive section; PMs (who never see
    // that section) get it here. The API already serves any signed-in user.
    ...(isPM || pmTestMode ? [{ path: "/resources", label: "Resources", icon: BookOpen }] : []),
    { path: "/studio/resource-center", labelKey: "nav.resourceCenter", icon: Users },
    { path: "/studio/leaves", label: "Leaves & Time Off", icon: CalendarOff },
    ...(canViewChangeOrders ? [{ path: "/change-orders", label: "Change Orders", icon: FileText }] : []),
    ...(!isPM && !pmTestMode ? [{ path: "/studio/performance", label: "Artist Performance", icon: Activity }] : []),
    { path: "/studio/management", labelKey: "nav.management", icon: FolderKanban },
    ...(!isPM && !pmTestMode ? [{ path: "/studio/timeline-settings", label: "Timeline Engine", icon: Clock }] : []),
    ...(!isPM && !pmTestMode ? [{ path: "/studio/vendors", label: "Vendors", icon: Building2 }] : []),
  ], [isPM, pmTestMode, canViewChangeOrders]);

  const executiveItems = useMemo(() =>
    isAdmin && !pmTestMode && !artistTestMode ? [
      { path: "/resources", label: "Resources", icon: BookOpen },
      { path: "/studio/artists/timeline", labelKey: "nav.artistTimeline", icon: CalendarIcon },
      { path: "/pnl", label: "P&L", icon: TrendingUp },
      { path: "/vexos-executive", label: "VEXOS Executive", icon: Command },
    ] : []
  , [isAdmin, pmTestMode, artistTestMode]);

  const adminItems = useMemo(() =>
    isAdmin && !pmTestMode && !artistTestMode ? [
      { path: "/chat", label: "TRP Chat", icon: MessageSquare },
      { path: "/admin/users", labelKey: "nav.userManagement", icon: Users },
      { path: "/admin/roles", label: "Roles & Permissions", icon: Shield },
      { path: "/admin/tracker", label: "Tracker Admin", icon: Mail },
      { path: "/admin/activity-log", label: "Activity Monitor", icon: Terminal },
    ] : []
  , [isAdmin, pmTestMode, artistTestMode]);

  // Smart Orange - COO/Admin only
  const smartOrangeItems = useMemo(() =>
    isAdmin && !pmTestMode && !artistTestMode ? [
      { path: "/smart-orange", labelKey: "nav.smartOrange", icon: Zap },
    ] : []
  , [isAdmin, pmTestMode, artistTestMode]);

  // Work in Progress — pages that are still being built. They are parked in one
  // section at the bottom instead of being mixed in with the finished tools, and
  // each one keeps exactly the permission gate it had in its old section.
  const wipItems = useMemo(() => [
    { path: "/meetings", label: "Meetings", icon: Video },
    ...(canViewFinancials || isAdmin ? [{ path: "/sales/quote-links", label: "Quote Links", icon: Link2 }] : []),
    ...(!isPM && !pmTestMode ? [{ path: "/sales-pipeline", label: "Sales Pipeline", icon: GitBranch }] : []),
    { path: "/archviz-planner", label: "ArchViz Planner", icon: ClipboardList },
    { path: "/review-studio", label: "Review Studio", icon: Eye },
    ...(isAdmin && !pmTestMode && !artistTestMode ? [
      ...(canViewFinancials ? [{ path: "/financials", label: "Financials", icon: Landmark }] : []),
      { path: "/planning-command-center", label: "Planning Command Center", icon: LayoutDashboard },
      { path: "/ai-workflows", label: "AI Workflows", icon: GitBranch },
    ] : []),
  ], [isAdmin, isPM, pmTestMode, artistTestMode, canViewFinancials]);

  // Helper to get label - use translation if labelKey exists, otherwise use label
  const getLabel = (item) => item.labelKey ? t(item.labelKey) : item.label;

  // VEXOS Executive role: minimal sidebar, nothing else to expand
  if (userRole === "VEXOS Executive") {
    return (
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <SidebarSection
          id="vexos-executive"
          label="VEXOS"
          items={vexosExecutiveItems}
          isExpanded={true}
          onToggle={() => {}}
          onNavClick={onNavClick}
        />
      </nav>
    );
  }

  // Sales Assistant role: minimal sidebar, nothing else to expand
  if (userRole === "Sales Assistant") {
    return (
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <SidebarSection
          id="sales-assistant"
          label="Sales"
          items={salesAssistantItems}
          isExpanded={true}
          onToggle={() => {}}
          onNavClick={onNavClick}
        />
      </nav>
    );
  }

  // If Artist role or Artist Test Mode, show minimal sidebar
  if (isArtist || artistTestMode) {
    return (
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <SidebarSection
          id="artist"
          label="My Work"
          items={artistItems}
          isExpanded={true}
          onToggle={() => {}}
          onNavClick={onNavClick}
        />
      </nav>
    );
  }

  return (
    <nav className="space-y-2 pb-6">
      <SidebarSection
        id="overview"
        label="Overview"
        items={overviewItems}
        isExpanded={expandedSections.overview}
        onToggle={toggleSection}
        onNavClick={onNavClick}
      />

      <SidebarSection
        id="work"
        label="Work"
        items={workItems}
        isExpanded={expandedSections.work}
        onToggle={toggleSection}
        onNavClick={onNavClick}
      />

      {salesItems.length > 0 && (
        <SidebarSection
          id="sales"
          label="Sales"
          items={salesItems}
          isExpanded={expandedSections.sales}
          onToggle={toggleSection}
          onNavClick={onNavClick}
        />
      )}

      <SidebarSection
        id="studio"
        label="Studio"
        items={studioItems}
        isExpanded={expandedSections.studio}
        onToggle={toggleSection}
        onNavClick={onNavClick}
      />

      {executiveItems.length > 0 && (
        <SidebarSection
          id="executive"
          label="Executive"
          items={executiveItems}
          isExpanded={expandedSections.executive}
          onToggle={toggleSection}
          onNavClick={onNavClick}
        />
      )}

      {adminItems.length > 0 && (
        <SidebarSection
          id="admin"
          label="Admin"
          items={adminItems}
          isExpanded={expandedSections.admin}
          onToggle={toggleSection}
          onNavClick={onNavClick}
        />
      )}

      {/* Smart Orange - External Sales Tool */}
      {smartOrangeItems.length > 0 && (
        <SidebarSection
          id="smartorange"
          label="External"
          items={smartOrangeItems}
          isExpanded={expandedSections.smartorange !== false}
          onToggle={toggleSection}
          onNavClick={onNavClick}
        />
      )}

      {/* Work in Progress - unfinished pages, last so the sections above stay clean */}
      {wipItems.length > 0 && (
        <SidebarSection
          id="wip"
          label="Work in Progress"
          items={wipItems}
          isExpanded={expandedSections.wip}
          onToggle={toggleSection}
          onNavClick={onNavClick}
        />
      )}
    </nav>
  );
});

// ============================================
// SIDEBAR FOOTER - Apple-esque styling
// ============================================
const SidebarFooter = memo(function SidebarFooter({ userName, userRole, onLogout, onNavClick }) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState(null);
  
  const footerButtonStyle = (itemId) => ({
    color: 'rgba(255,255,255,0.72)',
    backgroundColor: hoveredItem === itemId ? 'rgba(255,255,255,0.04)' : 'transparent',
  });
  
  return (
    <div className="p-4 space-y-1 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        onMouseEnter={() => setHoveredItem('theme')}
        onMouseLeave={() => setHoveredItem(null)}
        className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium tracking-[0.02em] transition-all duration-[120ms] ease-out w-full"
        style={footerButtonStyle('theme')}
        data-testid="theme-toggle"
      >
        {theme === "dark" ? (
          <>
            <Sun className="w-4 h-4" style={{ opacity: 0.7 }} strokeWidth={1.5} />
            {t("settings.appearance.light")}
          </>
        ) : (
          <>
            <Moon className="w-4 h-4" style={{ opacity: 0.7 }} strokeWidth={1.5} />
            {t("settings.appearance.dark")}
          </>
        )}
      </button>
      
      <NavLink
        to="/settings"
        onClick={onNavClick}
        className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium tracking-[0.02em] transition-all duration-[120ms] ease-out"
        style={({ isActive }) => ({
          color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
          backgroundColor: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
        })}
        onMouseEnter={(e) => {
          if (!e.currentTarget.classList.contains('active')) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
          }
        }}
        onMouseLeave={(e) => {
          const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
        data-testid="nav-settings"
      >
        <Settings className="w-4 h-4" style={{ opacity: 0.7 }} strokeWidth={1.5} />
        {t("nav.settings")}
      </NavLink>
      
      <button
        onClick={onLogout}
        onMouseEnter={() => setHoveredItem('logout')}
        onMouseLeave={() => setHoveredItem(null)}
        className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium tracking-[0.02em] transition-all duration-[120ms] ease-out w-full"
        style={footerButtonStyle('logout')}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" style={{ opacity: 0.7 }} strokeWidth={1.5} />
        {t("nav.logout")}
      </button>

      <UserProfile name={userName} role={userRole} />
    </div>
  );
});

// ============================================
// MAIN LAYOUT COMPONENT
// ============================================
const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("Program Manager");
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // "Request" in the top bar files a Help Desk request from whatever page
  // someone is stuck on, without leaving it.
  const [requestOpen, setRequestOpen] = useState(false);
  
  // Admin notification badge count
  const [intakeNotificationCount, setIntakeNotificationCount] = useState(0);
  
  const sidebarScrollRef = useRef(null);
  
  // PM View Toggle State
  const [pmTestMode, setPmTestMode] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState("");
  const [selectedPmName, setSelectedPmName] = useState("");
  const [pmList, setPmList] = useState([]);
  const [loadingPms, setLoadingPms] = useState(false);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('sidebarSections');
    if (saved) {
      try { return JSON.parse(saved); } 
      catch { return { overview: true }; }
    }
    return { overview: true };
  });

  // Permission checks - memoized
  const canUsePmTestMode = user?.role === "COO" || user?.role === "Admin";
  const isAdmin = user?.role === "COO" || user?.role === "Admin";
  const isPM = user?.role === "Program Manager" || user?.role === "Marketing PM";
  const isArtist = user?.role === "Artist";
  const canViewCosting = ["COO", "Admin", "Sales", "Accountant"].includes(user?.role) && !pmTestMode;
  
  // Artist test mode state
  const [artistTestMode, setArtistTestMode] = useState(false);
  const [selectedArtistName, setSelectedArtistName] = useState("");

  // Fetch admin notification count for intake alerts
  useEffect(() => {
    if (isAdmin && !pmTestMode) {
      const fetchNotificationCount = async () => {
        try {
          const response = await apiClient.get("/admin/notifications/intake-count");
          setIntakeNotificationCount(response.data.count || 0);
        } catch (err) {
          console.error("Failed to fetch notification count:", err);
        }
      };
      fetchNotificationCount();
      // Poll every 60 seconds
      const interval = setInterval(fetchNotificationCount, 60000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, pmTestMode]);

  // Mark notifications as read when visiting Pipeline
  useEffect(() => {
    if (isAdmin && (location.pathname === "/ops/pipeline" || location.pathname === "/ops/programs/awaiting-activation") && intakeNotificationCount > 0) {
      const markAsRead = async () => {
        try {
          await apiClient.post("/admin/notifications/mark-intakes-read");
          setIntakeNotificationCount(0);
        } catch (err) {
          console.error("Failed to mark notifications as read:", err);
        }
      };
      markAsRead();
    }
  }, [location.pathname, isAdmin, intakeNotificationCount]);

  // Auto-expand section based on route
  useEffect(() => {
    const path = location.pathname;
    let sectionToExpand = null;
    
    if (path === "/" || path === "/executive-command-center" || path === "/help-desk") {
      sectionToExpand = "overview";
    } else if (WIP_PATHS.some((wip) => path === wip || path.startsWith(`${wip}/`))) {
      sectionToExpand = "wip";
    } else if (path.startsWith("/programs") || path === "/money" || path.startsWith("/studio/project-costing") || path.startsWith("/portfolio")) {
      sectionToExpand = "work";
    } else if (path === "/resources" && (isPM || pmTestMode)) {
      sectionToExpand = "studio";
    } else if (path === "/resources" || path.startsWith("/studio/artists/timeline") || path === "/pnl" || path === "/vexos-executive") {
      sectionToExpand = "executive";
    } else if (path === "/chat") {
      sectionToExpand = "admin";
    } else if (path.startsWith("/sales") || path.startsWith("/whiteboard") || path.startsWith("/ops")) {
      sectionToExpand = "sales";
    } else if (path === "/change-orders" || path.startsWith("/studio")) {
      sectionToExpand = "studio";
    } else if (path.startsWith("/admin")) {
      sectionToExpand = "admin";
    }
    
    if (sectionToExpand && !expandedSections[sectionToExpand]) {
      setExpandedSections(prev => ({ ...prev, [sectionToExpand]: true }));
    }
  }, [location.pathname]);

  // Desktop sidebar collapse — remembered per browser so a user who likes the
  // wide canvas keeps it across reloads. Mobile uses its own drawer instead.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed)); } catch { /* storage blocked */ }
  }, [sidebarCollapsed]);
  const toggleSidebar = useCallback(() => setSidebarCollapsed(c => !c), []);

  // Browser fullscreen (hides the browser's own tabs/address bar too).
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => toast.error("Fullscreen isn't available in this browser"));
  }, []);

  // Cmd/Ctrl + \ toggles the sidebar.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') { e.preventDefault(); toggleSidebar(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleSidebar]);

  // Save sections state
  useEffect(() => {
    localStorage.setItem('sidebarSections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // Scroll position preservation
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebarScroll');
    if (savedScroll && sidebarScrollRef.current) {
      requestAnimationFrame(() => {
        const scrollArea = sidebarScrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollArea) scrollArea.scrollTop = parseInt(savedScroll, 10);
      });
    }
  }, [location.pathname]);

  const handleNavClick = useCallback(() => {
    if (sidebarScrollRef.current) {
      const scrollArea = sidebarScrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) sessionStorage.setItem('sidebarScroll', scrollArea.scrollTop.toString());
    }
    setMobileMenuOpen(false);
  }, []);

  // PM Test Mode handlers
  useEffect(() => {
    if (pmTestMode && pmList.length === 0) fetchPmList();
  }, [pmTestMode]);

  const fetchPmList = async () => {
    setLoadingPms(true);
    try {
      const response = await apiClient.get("/resources?resource_type=PROJECT_MANAGER");
      const pms = (response.data || [])
        .filter(r => r.status === "Active")
        .map(r => ({ id: r.id, display_name: r.display_name || r.legal_name || "Unknown PM", pm_type: r.pm_type }));
      setPmList(pms);
    } catch { setPmList([]); }
    setLoadingPms(false);
  };

  const handlePmSelect = useCallback((pmId, pmName) => {
    setSelectedPmId(pmId);
    setSelectedPmName(pmName);
    localStorage.setItem("pmTestMode", JSON.stringify({ pmId, pmName }));
    toast.success(`Now viewing as: ${pmName}`);
  }, []);

  const exitPmTestMode = useCallback(() => {
    setPmTestMode(false);
    setSelectedPmId("");
    setSelectedPmName("");
    localStorage.removeItem("pmTestMode");
    toast.success("Exited PM Test Mode");
  }, []);

  // Restore PM test mode
  useEffect(() => {
    if (canUsePmTestMode) {
      const saved = localStorage.getItem("pmTestMode");
      if (saved) {
        try {
          const { pmId, pmName } = JSON.parse(saved);
          setPmTestMode(true);
          setSelectedPmId(pmId);
          setSelectedPmName(pmName);
        } catch { localStorage.removeItem("pmTestMode"); }
      }
    }
  }, [canUsePmTestMode]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = useCallback(() => { logout(); navigate("/login"); }, [logout, navigate]);

  const resetInviteForm = useCallback(() => {
    setShowInvite(false);
    setInviteEmail("");
    setInviteName("");
    setInviteRole("Program Manager");
    setInviteLink("");
    setCopied(false);
  }, []);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !inviteName.trim()) { toast.error("Please fill in all fields"); return; }
    setSending(true);
    try {
      const response = await apiClient.post("/invites", { email: inviteEmail, name: inviteName, role: inviteRole });
      setInviteLink(response.data.invite_link);
      toast.success(`Invitation created for ${inviteName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally { setSending(false); }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Failed to copy link"); }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join The Render Pros`,
          text: `${user?.name} invited you to join The Render Pros Operations OS as a ${inviteRole}.`,
          url: inviteLink
        });
      } catch (err) { if (err.name !== 'AbortError') toast.error("Failed to share"); }
    } else { handleCopyLink(); }
  };

  // Desktop offset for everything that sits beside the sidebar.
  const contentLeft = sidebarCollapsed ? 'left-0' : 'left-60';

  return (
    <div className="min-h-screen bg-background">
      {/* ============================================ */}
      {/* MOBILE HEADER */}
      {/* ============================================ */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 z-40 flex items-center justify-between px-4" style={{ backgroundColor: '#000000', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <img src="/trp-logo.png" alt="The Render Pros" className="h-6 object-contain opacity-[0.92]" loading="eager" decoding="sync" />
        <div className="flex items-center gap-2">
          <NotificationDropdown />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg transition-colors duration-[120ms]"
            style={{ color: 'rgba(255,255,255,0.72)' }}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div 
        className={`md:hidden fixed inset-0 bg-ink/50 backdrop-blur-[3px] z-40 transition-opacity duration-250 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile sidebar */}
      <aside
        className="md:hidden fixed top-14 left-0 bottom-0 w-72 z-50 flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] select-none"
        style={{
          backgroundColor: '#000000',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          visibility: mobileMenuOpen ? 'visible' : 'hidden',
          transitionProperty: 'transform, visibility',
          transitionDuration: '300ms, 0s',
          transitionDelay: mobileMenuOpen ? '0s, 0s' : '0s, 300ms',
        }}
      >
        <SidebarLogo />
        <ScrollArea className="flex-1 px-3" ref={sidebarScrollRef}>
          <SidebarNavigation
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            onNavClick={handleNavClick}
            pmTestMode={pmTestMode}
            artistTestMode={artistTestMode}
            isPM={isPM}
            isArtist={isArtist}
            isAdmin={isAdmin}
            canViewCosting={canViewCosting}
            intakeNotificationCount={intakeNotificationCount}
            userEmail={user?.email}
            userRole={user?.role}
          />
        </ScrollArea>
        <SidebarFooter userName={user?.name} userRole={user?.role} onLogout={handleLogout} onNavClick={handleNavClick} />
      </aside>

      {/* ============================================ */}
      {/* DESKTOP SIDEBAR - Apple-esque design */}
      {/* ============================================ */}
      <aside 
        className="hidden md:flex w-60 flex-col h-screen fixed left-0 top-0 z-30 select-none transition-transform duration-200 ease-out"
        style={{ 
          backgroundColor: '#000000', 
          borderRight: '1px solid rgba(255,255,255,0.06)',
          transform: sidebarCollapsed ? 'translateX(-100%)' : 'translateX(0)',
        }}
        aria-hidden={sidebarCollapsed}
        inert={sidebarCollapsed}
      >
        {/* Logo is completely isolated and never re-renders */}
        <SidebarLogo />
        
        {/* Scrollable navigation area */}
        <ScrollArea className="flex-1 px-3" ref={sidebarScrollRef}>
          <SidebarNavigation
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            onNavClick={handleNavClick}
            pmTestMode={pmTestMode}
            artistTestMode={artistTestMode}
            isPM={isPM}
            isArtist={isArtist}
            isAdmin={isAdmin}
            canViewCosting={canViewCosting}
            intakeNotificationCount={intakeNotificationCount}
            userEmail={user?.email}
            userRole={user?.role}
          />
        </ScrollArea>
        
        {/* Footer */}
        <SidebarFooter userName={user?.name} userRole={user?.role} onLogout={handleLogout} onNavClick={handleNavClick} />
      </aside>

      {/* PM Test Mode Banner */}
      {pmTestMode && selectedPmName && (
        <div className={`fixed top-0 ${contentLeft} right-0 h-10 bg-sage-700 text-white z-30 flex items-center justify-center gap-4 text-sm font-medium`}>
          <Eye className="w-4 h-4" />
          <span>PM Test Mode: Viewing as <strong>{selectedPmName}</strong></span>
          <button onClick={exitPmTestMode} className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs flex items-center gap-1 transition-colors duration-150" data-testid="exit-pm-test-mode">
            <X className="w-3 h-3" /> Exit
          </button>
        </div>
      )}

      {/* Artist Test Mode Banner */}
      {artistTestMode && selectedArtistName && (
        <div className={`fixed top-0 ${contentLeft} right-0 h-10 bg-green-700 text-white z-30 flex items-center justify-center gap-4 text-sm font-medium`}>
          <Eye className="w-4 h-4" />
          <span>Artist Mode: Viewing as <strong>{selectedArtistName}</strong></span>
          <button onClick={() => { 
            setArtistTestMode(false); 
            setSelectedArtistName(""); 
            localStorage.removeItem('artistTestMode');
            localStorage.removeItem('artistTestName');
          }} className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs flex items-center gap-1 transition-colors duration-150" data-testid="exit-artist-test-mode">
            <X className="w-3 h-3" /> Exit
          </button>
        </div>
      )}

      {/* Desktop Top Bar */}
      <header className={`hidden md:flex fixed ${(pmTestMode && selectedPmName) || (artistTestMode && selectedArtistName) ? 'top-10' : 'top-0'} ${contentLeft} right-0 h-14 border-b border-border bg-background z-20 items-center justify-end px-6 gap-4 transition-[left] duration-200 ease-out`}>
        {/* Sidebar + fullscreen toggles, pinned to the left of the bar */}
        <div className="mr-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
            title={sidebarCollapsed ? "Show menu (⌘\)" : "Hide menu (⌘\)"}
            aria-label={sidebarCollapsed ? "Show menu" : "Hide menu"}
            data-testid="toggle-sidebar"
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
            title={isFullscreen ? "Exit full screen" : "Full screen"}
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            data-testid="toggle-fullscreen"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </Button>
        </div>
        {canUsePmTestMode && !artistTestMode && (
          <div className="flex items-center gap-2">
            {!pmTestMode ? (
              <Button variant="ghost" size="sm" onClick={() => { setPmTestMode(true); fetchPmList(); }} className="text-muted-foreground hover:text-purple-600 transition-colors duration-150" data-testid="enable-pm-test-mode">
                <Eye className="w-4 h-4 mr-1.5" />PM View
              </Button>
            ) : !selectedPmId ? (
              <Select onValueChange={(val) => { const pm = pmList.find(p => p.id === val); if (pm) handlePmSelect(pm.id, pm.display_name); }}>
                <SelectTrigger className="w-[180px] h-8 text-sm border-purple-300" data-testid="pm-select-dropdown">
                  <SelectValue placeholder={loadingPms ? "Loading..." : "Select PM"} />
                </SelectTrigger>
                <SelectContent>
                  {pmList.map(pm => <SelectItem key={pm.id} value={pm.id}>{pm.display_name}</SelectItem>)}
                  {pmList.length === 0 && !loadingPms && <SelectItem value="none" disabled>No PMs found</SelectItem>}
                </SelectContent>
              </Select>
            ) : null}
          </div>
        )}
        
        {/* Artist View Toggle */}
        {canUsePmTestMode && !pmTestMode && (
          <div className="flex items-center gap-2">
            {!artistTestMode ? (
              <Button variant="ghost" size="sm" onClick={async () => {
                // Use Bruno Corigliano as the demo artist
                try {
                  const res = await apiClient.get("/resources?type=ARTIST");
                  const artists = res.data || [];
                  // Find Bruno Corigliano specifically, or fall back to first artist
                  const bruno = artists.find(a => a.display_name?.toLowerCase().includes("bruno")) || artists[0];
                  if (bruno) {
                    setArtistTestMode(true);
                    setSelectedArtistName(bruno.display_name || "Bruno Corigliano");
                    localStorage.setItem('artistTestMode', 'true');
                    localStorage.setItem('artistTestName', bruno.display_name || "Bruno Corigliano");
                  }
                } catch (e) { console.error(e); }
              }} className="text-muted-foreground hover:text-emerald-600 transition-colors duration-150" data-testid="enable-artist-test-mode">
                <Eye className="w-4 h-4 mr-1.5" />Artist View
              </Button>
            ) : null}
          </div>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRequestOpen(true)}
          className="text-muted-foreground hover:text-foreground"
          title="Ask HQ for a fix, a feature or access"
          data-testid="header-new-request"
        >
          <LifeBuoy className="w-4 h-4 mr-1.5" />Request
        </Button>

        {/* Language Switcher - before notifications */}
        <LanguageSwitcher />
        
        {/* Notifications */}
        <NotificationDropdown />
        
        <span className="text-sm text-muted-foreground">{user?.name}</span>
      </header>

      <NewRequestDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        pageUrl={`${location.pathname}${location.search}`}
        viewLink
      />

      {/* ============================================ */}
      {/* MAIN CONTENT - Only this re-renders on route change */}
      {/* ============================================ */}
      <main className={`${sidebarCollapsed ? 'md:ml-0' : 'md:ml-60'} transition-[margin] duration-200 ease-out ${(pmTestMode && selectedPmName) || (artistTestMode && selectedArtistName) ? 'pt-24' : 'pt-14'}`}>
        <div className="w-full p-4 md:p-6 lg:p-8 animate-fade-in">
          {children}
        </div>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={(open) => { if (!open) resetInviteForm(); else setShowInvite(true); }}>
        <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto mx-4">
          {!inviteLink ? (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Invite Team Member</DialogTitle>
                <DialogDescription>Add a new team member to TRP Operations OS.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Full Name</Label>
                  <Input id="invite-name" placeholder="John Smith" value={inviteName} onChange={(e) => setInviteName(e.target.value)} data-testid="invite-name-input" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email Address</Label>
                  <Input id="invite-email" type="email" placeholder="john@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger id="invite-role" data-testid="invite-role-select" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Program Manager">Program Manager</SelectItem>
                      <SelectItem value="Production">Production</SelectItem>
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="COO">COO (Admin)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {inviteRole === "COO" && "Full access to all features and settings"}
                    {inviteRole === "Program Manager" && "Manage assigned programs and deliverables"}
                    {inviteRole === "Production" && "View and update production status"}
                    {inviteRole === "Sales" && "View programs and financial data"}
                  </p>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={resetInviteForm} className="rounded-full w-full sm:w-auto">Cancel</Button>
                <Button onClick={handleSendInvite} disabled={sending || !inviteEmail.trim() || !inviteName.trim()} className="rounded-full w-full sm:w-auto" data-testid="send-invite-btn">
                  {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Creating...</> : <><Link2 className="w-4 h-4 mr-2" />Create Invite Link</>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center justify-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Check className="w-6 h-6 text-green-600" /></div>
                </div>
                <DialogTitle className="text-center" style={{ fontFamily: 'var(--font-sans)' }}>Invite Created!</DialogTitle>
                <DialogDescription className="text-center">Share this link with <strong>{inviteName}</strong> to join as {inviteRole}</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-secondary/50 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-2 mb-3"><Link2 className="w-4 h-4 text-muted-foreground" /><span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Invite Link</span></div>
                  <div className="bg-white rounded-md border border-border p-3"><p className="text-sm text-foreground break-all font-mono">{inviteLink}</p></div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button onClick={handleCopyLink} variant="outline" className="flex-1 rounded-full h-11" data-testid="copy-link-btn">
                    {copied ? <><Check className="w-4 h-4 mr-2 text-green-600" />Copied!</> : <><Copy className="w-4 h-4 mr-2" />Copy Link</>}
                  </Button>
                  <Button onClick={handleShare} className="flex-1 rounded-full h-11" data-testid="share-link-btn"><Share2 className="w-4 h-4 mr-2" />Share</Button>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-4">This invite link expires in 7 days</p>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-center">
                <Button variant="ghost" onClick={resetInviteForm} className="rounded-full w-full sm:w-auto">Done</Button>
                <Button variant="outline" onClick={() => { setInviteLink(""); setInviteEmail(""); setInviteName(""); }} className="rounded-full w-full sm:w-auto"><UserPlus className="w-4 h-4 mr-2" />Invite Another</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
